<template>
  <div class="outer-container">
    <span>
      Who should this notification go to?  Click the ADD button to add them to the list.
    </span>
    <div class="inner-container">
      <div class="ves-user-container" :class="{ 'disabled-text': vesUsersDisabled }">
        <h3>All VRpatients Users</h3>
        <md-checkbox v-model="state.vesCategories" :disabled="vesUsersDisabled" value="USERS" @change="changeVesEveryone()">Everyone</md-checkbox>
        <md-checkbox v-model="state.vesCategories" :disabled="vesUsersDisabled" value="STUDENTS" @change="changeVesSpecific()">Students</md-checkbox>
        <md-checkbox v-model="state.vesCategories" :disabled="vesUsersDisabled" value="INSTRUCTORS" @change="changeVesSpecific()">Instructors</md-checkbox>
        <md-checkbox v-model="state.vesCategories" :disabled="vesUsersDisabled" value="ADMINS" @change="changeVesSpecific()">Admins</md-checkbox>
        <md-checkbox v-model="state.vesCategories" :disabled="vesUsersDisabled" value="KEYOWNERS" @change="changeVesSpecific()">Key Owners</md-checkbox>
      </div>
      <div class="organization-container" :class="{ 'disabled-text': organizationsDisabled }">
        <h3>Organizations</h3>
        <md-autocomplete
          ref="orgAutocomplete"
          :md-options="orgs"
          :md-fuzzy-search="false"
          :md-open-on-focus="true"
          :value="state.selectedOrg ? state.selectedOrg.name : ''"
          @md-selected="selectOrg"
        >
          <label>Organization</label>
          <template slot="md-autocomplete-item" slot-scope="{ item, term }">
            <md-highlight-text :md-term="term">{{ item.name }}</md-highlight-text>
          </template>
        </md-autocomplete>
        <md-checkbox v-model="state.orgCategories" :disabled="organizationsDisabled" value="USERS" @change="changeOrgEveryone()">Everyone</md-checkbox>
        <md-checkbox v-model="state.orgCategories" :disabled="organizationsDisabled" value="STUDENTS" @change="changeOrgSpecific()">Students</md-checkbox>
        <md-checkbox v-model="state.orgCategories" :disabled="organizationsDisabled" value="INSTRUCTORS" @change="changeOrgSpecific()">Instructors</md-checkbox>
        <md-checkbox v-model="state.orgCategories" :disabled="organizationsDisabled" value="ADMINS" @change="changeOrgSpecific()">Admins</md-checkbox>
        <md-checkbox v-model="state.orgCategories" :disabled="organizationsDisabled" value="KEYOWNERS" @change="changeOrgSpecific()">Key Owners</md-checkbox>
      </div>
      <div class="users-container" :class="{ 'disabled-text': usersDisabled }">
        <h3>Users</h3>
        <md-autocomplete
          ref="userAutocomplete"
          :md-options="usersWithLabels"
          :md-open-on-focus="true"
          :value="state.selectedUser ? userLabel(state.selectedUser) : ''"
          @md-selected="selectUser"
          @md-changed="changeUser"
        >
          <label>User</label>
          <template slot="md-autocomplete-item" slot-scope="{ item, term }">
            <md-highlight-text :md-term="term">{{ item.label }}</md-highlight-text>
          </template>
        </md-autocomplete>
      </div>
    </div>
    <md-button class="md-raised md-primary add-button" :disabled="addDisabled" @click="add">
      <md-icon>add</md-icon>
      ADD
    </md-button>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import { userLabel } from '~lib/common/utils/labels';

export default {
  name: 'notification-targets',
  props: {    
    orgs: {
      type: Array,
      default: () => []
    },
    users: {
      type: Array,
      default: () => []
    },
  },
  data() {
    return {
      state: {
        vesCategories: [],
        orgCategories: [],
        selectedOrg: null,
        selectedUser: null,
      },
    };
  },
  computed: {
    vesUsersDisabled() {
      return this.state.orgCategories.length > 0 || !!this.state.selectedOrg || !!this.state.selectedUser;
    },
    organizationsDisabled() {
      return this.state.vesCategories.length > 0 || !!this.state.selectedUser;
    },
    usersDisabled() {
      return this.state.vesCategories.length > 0 || this.state.orgCategories.length || !!this.state.selectedOrg;
    },
    addDisabled() {
      return this.state.vesCategories.length <= 0 && 
        (this.state.orgCategories.length <= 0 || !this.state.selectedOrg) &&
        !this.state.selectedUser;
    },
    usersWithLabels() {
      return this.users.map(user => {
        return {
          ...user,
          label: userLabel(user),
        };
      });
    },
  },
  methods: {
    changeVesEveryone() {
      this.state.vesCategories = this.state.vesCategories.filter(u => u === 'USERS');
    },
    changeVesSpecific() {
      this.state.vesCategories = this.state.vesCategories.filter(u => u !== 'USERS');
    },
    changeOrgEveryone() {
      this.state.orgCategories = this.state.orgCategories.filter(u => u === 'USERS');
    },
    changeOrgSpecific() {
      this.state.orgCategories = this.state.orgCategories.filter(u => u !== 'USERS');
    },
    selectOrg(org) {
      this.state.selectedOrg = org;
      this.$nextTick(() => {
        // Force close the autocomplete, vue material bug
        if (this.$refs.orgAutocomplete) {
          this.$refs.orgAutocomplete.showMenu = false;          
        }
      });
    },
    selectUser(user) {
      this.state.selectedUser = user;
      this.$nextTick(() => {
        // Force close the autocomplete, vue material bug
        if (this.$refs.userAutocomplete) {
          this.$refs.userAutocomplete.showMenu = false;          
        }
      });
    },
    changeUser: debounce(function changeUser(term) {
      if (term.length > 0 && term.length < 3) {
        return;
      }
      this.$emit('filterUsers', term);
    }, 500),
    add() {
      let targets = [];

      const labels = {
        'USERS': 'Users',
        'ADMINS': 'Admins',
        'INSTRUCTORS': 'Instructors',
        'STUDENTS': 'Students',
        'KEYOWNERS': 'Key Owners'
      }

      if (this.state.vesCategories.length) {
        targets = this.state.vesCategories.map(cat => {
          return {
            principalType: 'GROUP',
            principalId: `ALL_${cat}`,
            label: `All ${labels[cat]}`,
          }
        });
      } else if (this.state.orgCategories.length) {
        const org = this.state.selectedOrg;
        targets = this.state.orgCategories.map(cat => {
          const principalId = cat === 'USERS' ? 
            org._id : `${org._id}_${cat}`;
          return {
            principalType: 'GROUP',
            principalId,
            label: `Org ${org.name}: ${labels[cat]}`,
          }
        });
      } else if (this.state.selectedUser) {
        targets = [{
          principalType: 'USER',
          principalId: this.state.selectedUser.user_id,
          label: userLabel(this.state.selectedUser),
        }];
      }

      this.$emit('add', targets);
      this.state.vesCategories = [];
      this.state.orgCategories = [];
      this.state.selectedOrg = null;
      this.state.selectedUser = null;
      this.$refs.orgAutocomplete.searchTerm = '';
      this.$refs.userAutocomplete.searchTerm = '';
    },
    userLabel,
  }
}
</script>

<style scoped>
.outer-container {
  display: flex;
  flex-direction: column;
}

.inner-container {
  display: flex;
}

.ves-user-container {
  display: block;
  flex-direction: column;
  margin-right: 30px;
}

.organization-container {
  display: block;
  flex-direction: column;
  margin-right: 30px;
}

.user-container {
  display: block;
  flex-direction: column;
}

h3 {
  margin-block-start: 1em;
  margin-block-end: .5em;
}

.disabled-text {
  color: gray;
}

.md-checkbox {
  display: flex;
}

.md-button:not(.md-icon-button) {
    border-radius: 16px;
}

.add-button {
  align-self: flex-end;
}

</style>